import { Text, Wrap } from "@chakra-ui/react";
import { SubscriptionWithPlansAndAccess } from "@syla/shared/types/responses/GetSubscriptionsResponse";
import { getSubscriptionState } from "@syla/shared/types/subscriptions/getSubscriptionState";
import {
  Lifecycle,
  SubscriptionType,
  RenewalAction,
  BillingStatus,
} from "@syla/shared/types/subscriptions/subscriptionState";
import React from "react";
import { SettingRowBase } from "../components/SettingRowBase";
import { CancelPlanButton } from "./CancelPlanButton";
import { ChangePlanButton } from "./ChangePlanButton";
import { CreateManualSubscriptionButton } from "./CreateManualSubscriptionButton";
import { ManageBillingButton } from "./ManageBillingButton";
import { PlanStatusTag } from "./PlanStatusTag";
import { PurchasePlanButton } from "./PurchasePlanButton";
import { ResumePlanButton } from "./ResumePlanButton";

import { ValueAndButtons } from "./ValueAndButtons";

interface SubscriptionPlanDetailsProps {
  accountId: string;
  canManageExistingSubscription: boolean;
  subscription: SubscriptionWithPlansAndAccess | undefined;
  isSuperUser: boolean;
}

export const SubscriptionPlanDetails = ({
  accountId,
  canManageExistingSubscription,
  subscription,
  isSuperUser,
}: SubscriptionPlanDetailsProps) => {
  const state = subscription ? getSubscriptionState(subscription) : undefined;

  const planName = subscription ? subscription.plan.name : "Free";

  const showPurchase =
    !subscription || (state && state.lifecycle !== Lifecycle.Active);

  const showChange =
    state &&
    state.type === SubscriptionType.Stripe &&
    state.lifecycle === Lifecycle.Active &&
    canManageExistingSubscription;

  const showUndoDowngrade =
    state &&
    state.type === SubscriptionType.Stripe &&
    state.renewal === RenewalAction.Downgrade &&
    canManageExistingSubscription;

  const showManageBilling =
    state &&
    state.type === SubscriptionType.Stripe &&
    state.lifecycle === Lifecycle.Active &&
    state.billing === BillingStatus.Unpaid &&
    canManageExistingSubscription;

  const showCancel =
    state &&
    state.type === SubscriptionType.Stripe &&
    state.lifecycle === Lifecycle.Active &&
    (state.renewal === RenewalAction.Renew ||
      state.renewal === RenewalAction.Downgrade) &&
    canManageExistingSubscription;

  const showResume =
    state &&
    state.type === SubscriptionType.Stripe &&
    state.renewal === RenewalAction.Cancel &&
    canManageExistingSubscription;

  const showCreateManualSubscription =
    (!state ||
      state.type === SubscriptionType.Manual ||
      (state.type === SubscriptionType.Stripe &&
        state.lifecycle === Lifecycle.Terminated &&
        canManageExistingSubscription)) &&
    isSuperUser;

  return (
    <SettingRowBase label="Current plan">
      <ValueAndButtons>
        <Wrap>
          <Text>{planName}</Text>
          {state && <PlanStatusTag status={state.status} />}
        </Wrap>
        <Wrap>
          {showPurchase && <PurchasePlanButton accountId={accountId} />}

          {showCreateManualSubscription && <CreateManualSubscriptionButton />}

          {showChange && subscription?.stripeSubscriptionId && (
            <ChangePlanButton
              accountId={accountId}
              stripeSubscriptionId={subscription.stripeSubscriptionId}
            />
          )}

          {showUndoDowngrade && (
            <ResumePlanButton accountId={accountId} label={"Undo downgrade"} />
          )}

          {showManageBilling && (
            <ManageBillingButton accountId={accountId} label="Update billing" />
          )}

          {showCancel && subscription?.stripeSubscriptionId && (
            <CancelPlanButton
              accountId={accountId}
              stripeSubscriptionId={subscription.stripeSubscriptionId}
            />
          )}

          {showResume && (
            <ResumePlanButton accountId={accountId} label="Resume plan" />
          )}
        </Wrap>
      </ValueAndButtons>
    </SettingRowBase>
  );
};
